import type { ProviderToken } from '@angular/core';
import {
  inject,
  Injector,
  runInInjectionContext,
  Service,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxAddressByType,
  OnyxAddressType,
  OnyxDate,
  OnyxMaybeArray,
  OnyxNumberRange,
  OnyxOption,
  OnyxPhone,
  OnyxRoutePoint,
  OnyxTime,
  OnyxTimeRange,
} from '@onyx/angular';
import {
  DropdownHelper,
  OnyxLanguagePipe,
  PeselNumberHelper,
  UtilityHelper,
  VIN_NUMBER_PATTERN,
} from '@onyx/angular';
import type { CountryCode } from 'libphonenumber-js';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { castArray, chain, get, isBoolean, isString } from 'lodash';
import { DateTime } from 'luxon';
import {
  catchError,
  defaultIfEmpty,
  firstValueFrom,
  forkJoin,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import type { Paths } from 'type-fest';
import * as XLSX from 'xlsx';
import * as z from 'zod';
import { AuthStore } from '../../../auth/common/stores/auth.store';
import type {
  DictionaryCode,
  DictionaryResponseByCode,
} from '../../../common/enums/dictionary-code';
import type { CalculateRouteRequest } from '../../../common/interfaces/address/calculate-route-request';
import type { CompanyData } from '../../../common/interfaces/data-providers/company-data';
import type {
  ForbiddenField,
  RequiredField,
  ValidationField,
} from '../../../common/interfaces/validation/validation-field';
import { AddressService } from '../../../common/services/address.service';
import { DataProvidersService } from '../../../common/services/data-providers.service';
import { DictionariesService } from '../../../common/services/dictionaries.service';
import type { Base } from '../../management-panel/bases/common/interfaces/base';
import { BasesService } from '../../management-panel/bases/common/services/bases.service';
import { ContractorHelper } from '../../management-panel/contractors/common/helpers/contractor.helper';
import type { Contractor } from '../../management-panel/contractors/common/interfaces/contractor';
import type {
  CompanyDictionaryByCode,
  CompanyDictionaryCode,
} from '../../management-panel/dictionaries/common/enums/company-dictionary-code';
import { CompanyDictionariesService } from '../../management-panel/dictionaries/common/services/company-dictionaries.service';
import type { PointOfInterest } from '../../management-panel/points-of-interest/common/interfaces/point-of-interest';
import { PointsOfInterestService } from '../../management-panel/points-of-interest/common/services/points-of-interest.service';
import type {
  OrderFormDto,
  OrderLoadingPointForm,
} from '../../orders/common/helpers/order-form.helper';
import type { OrderRoute } from '../../orders/common/interfaces/order';
import { OrderFormRouteConfigModalComponent } from '../../orders/order-form/order-form-route/order-form-route-config-modal/order-form-route-config-modal.component';
import type { OrderUnloadingPointForm } from '../../orders/order-form/order-points-form/order-unloading-point-form/order-unloading-point-form.component';
import { BusinessService } from './../../management-panel/business/common/services/business.service';
import { ContractorsService } from './../../management-panel/contractors/common/services/contractors.service';

interface RefineOptionalFieldParams<Path extends string> {
  isRequired: boolean;
  value: any;
  path: Path[];
  context: z.RefinementCtx;
  missingMessage: string;
  forbiddenMessage: string;
}

@Service()
export class ImportHelper {
  private readonly authStore = inject(AuthStore);
  private readonly addressService = inject(AddressService);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly dataProvidersService = inject(DataProvidersService);
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly contractorsService = inject(ContractorsService);
  private readonly basesService = inject(BasesService);
  private readonly businessService = inject(BusinessService);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly pointsOfInterestService = inject(PointsOfInterestService);

  public readonly REQUIRED_FIELD: RequiredField = { type: 'required' } as const;
  public readonly FORBIDDEN_FIELD: ForbiddenField = {
    type: 'forbidden',
  } as const;

  public boolean<Nullable extends boolean>(options: {
    nullable: Nullable;
  }): Nullable extends false
    ? z.ZodPipe<z.ZodAny, z.ZodTransform<boolean, any>>
    : z.ZodPipe<
        z.ZodNullable<z.ZodAny>,
        z.ZodTransform<boolean | null, any | null>
      > {
    return z.any().transform((value, context) => {
      if (value == null) {
        if (options.nullable) return null;

        context.addIssue({
          code: 'custom',
          message: 'Expected boolean, received null',
        });
        return z.NEVER;
      }

      if (isBoolean(value)) return value;

      value = String(value).trim().toLowerCase();
      if (['true', '1'].includes(value)) return true;
      if (['false', '0'].includes(value)) return false;

      const localeYes = this.translateService
        .instant('labels.yes')
        .toLowerCase();
      const localeNo = this.translateService.instant('labels.no').toLowerCase();

      if (value === localeYes) return true;
      if (value === localeNo) return false;

      context.addIssue({
        code: 'custom',
        message: `Invalid boolean (${value})`,
      });
      return z.NEVER;
    }) as any;
  }

  public date<Nullable extends boolean>(options: {
    nullable: Nullable;
    allowIndefinite?: boolean;
    min?: DateTime;
    max?: DateTime;
  }): Nullable extends false
    ? z.ZodPipe<
        z.ZodUnion<[z.ZodNumber, z.ZodString]>,
        z.ZodTransform<string, number | string>
      >
    : z.ZodPipe<
        z.ZodNullable<z.ZodUnion<[z.ZodNumber, z.ZodString]>>,
        z.ZodTransform<string | null, number | string | null>
      > {
    const type = options.nullable
      ? z.union([z.number(), z.string()]).nullable()
      : z.union([z.number(), z.string()]);
    return type.transform((value, context) => {
      if (!value && options.nullable) return null;

      const stringValue = isString(value)
        ? value.trim().toLowerCase()
        : XLSX.SSF.format('yyyy-MM-dd', value);
      const browserDate = DateTime.fromJSDate(new Date(stringValue));
      const luxonDate = DateTime.fromFormat(stringValue, 'dd.MM.yyyy');

      const date = browserDate.isValid ? browserDate : luxonDate;
      if (!date.isValid) {
        if (options.allowIndefinite) {
          const localesIndefinite = [
            this.translateService.instant('labels.indefinite'),
            this.translateService.instant('labels.indefiniteTime'),
          ];
          if (
            localesIndefinite.some((localeIndefinite) =>
              stringValue.includes(localeIndefinite.toLowerCase()),
            )
          ) {
            return 'indefinite';
          }
        }

        context.addIssue({
          code: 'custom',
          message: `Invalid date (${value})`,
        });
        return z.NEVER;
      }

      if (options.min != null && date < options.min) {
        context.addIssue({
          code: 'custom',
          message: `Value must be greater than or equal to ${options.min.toISODate()} (${value})`,
        });
        return z.NEVER;
      }

      if (options.max != null && date > options.max) {
        context.addIssue({
          code: 'custom',
          message: `Value must be less than or equal to ${options.max.toISODate()} (${value})`,
        });
        return z.NEVER;
      }

      return date.toISODate();
    }) as any;
  }

  public decimal<Nullable extends boolean>(options: {
    decimalPlaces: number;
    nullable: Nullable;
    min?: number;
    max?: number;
  }): Nullable extends false
    ? z.ZodPipe<z.ZodNumber, z.ZodTransform<number, number>>
    : z.ZodPipe<
        z.ZodNullable<z.ZodNumber>,
        z.ZodTransform<number | null, number | null>
      > {
    return this.string({ nullable: options.nullable }).transform(
      (input, context) => {
        if (input == null) return null;

        const value = Number(input.trim().replace(',', '.'));
        if (isNaN(value)) {
          context.addIssue({
            code: 'custom',
            message: `Invalid decimal (${input})`,
          });
          return z.NEVER;
        }

        if (options.min != null && value < options.min) {
          context.addIssue({
            code: 'custom',
            message:
              options.min === Number.EPSILON
                ? `Value must be greater than 0 (${value})`
                : `Value must be greater than or equal to ${options.min} (${value})`,
          });
          return z.NEVER;
        }

        if (options.max != null && value > options.max) {
          context.addIssue({
            code: 'custom',
            message: `Value must be less than or equal to ${options.max} (${value})`,
          });
          return z.NEVER;
        }

        return Math.floor(value * 10 ** options.decimalPlaces);
      },
    ) as any;
  }

  public phone<Nullable extends boolean>(options: {
    nullable: Nullable;
  }): Nullable extends false
    ? z.ZodPipe<
        z.ZodUnion<[z.ZodNumber, z.ZodString]>,
        z.ZodTransform<OnyxPhone, string>
      >
    : z.ZodPipe<
        z.ZodNullable<z.ZodUnion<[z.ZodNumber, z.ZodString]>>,
        z.ZodTransform<OnyxPhone | null, string | null>
      > {
    const type = !options.nullable
      ? z.union([z.number(), z.string()])
      : z.union([z.number(), z.string()]).nullable();

    return type.transform((value, context) => {
      if (!value && options.nullable) return null;

      const phone = parsePhoneNumberFromString(String(value).trim(), {
        defaultCountry: this.authStore
          .business()!
          .country.toUpperCase() as CountryCode,
      });
      if (!phone) {
        context.addIssue({
          code: 'custom',
          message: `Invalid phone number (${value})`,
        });
        return z.NEVER;
      }

      return {
        areaCode: `+${phone.countryCallingCode}`,
        number: phone.nationalNumber,
      };
    }) as any;
  }

  public string<Nullable extends boolean>(options: {
    nullable: Nullable;
    length?: number;
    minLength?: number;
    maxLength?: number;
  }): Nullable extends false
    ? z.ZodPipe<z.ZodAny, z.ZodTransform<string, any>>
    : z.ZodPipe<z.ZodNullable<z.ZodAny>, z.ZodTransform<string | null, any>> {
    const type = !options.nullable ? z.any() : z.any().nullable();

    return type.transform((value, context) => {
      if (!value) {
        if (options.nullable) return null;

        context.addIssue({
          code: 'custom',
          message: 'Expected string, received null',
        });
        return z.NEVER;
      }

      value = String(value).trim();
      if (options.length != null && value.length !== options.length) {
        context.addIssue({
          code: 'custom',
          message: `Invalid length (received: ${value.length} (${value}), expected: ${options.length})`,
        });
        return z.NEVER;
      } else if (
        options.minLength != null &&
        value.length < options.minLength
      ) {
        context.addIssue({
          code: 'custom',
          message: `Invalid length (received: ${value.length} (${value}), minimum: ${options.minLength})`,
        });
        return z.NEVER;
      } else if (
        options.maxLength != null &&
        value.length > options.maxLength
      ) {
        context.addIssue({
          code: 'custom',
          message: `Invalid length (received: ${value.length} (${value}), maximum: ${options.maxLength})`,
        });
        return z.NEVER;
      }

      return value;
    }) as any;
  }

  public time<Nullable extends boolean>(options: {
    nullable: Nullable;
  }): Nullable extends false
    ? z.ZodPipe<
        z.ZodUnion<[z.ZodNumber, z.ZodString]>,
        z.ZodTransform<OnyxTime, number | string>
      >
    : z.ZodPipe<
        z.ZodNullable<z.ZodUnion<[z.ZodNumber, z.ZodString]>>,
        z.ZodTransform<OnyxTime | null, number | string | null>
      > {
    const FORMAT = 'HH:mm';
    const type = !options.nullable
      ? z.union([z.number(), z.string()])
      : z.union([z.number(), z.string()]).nullable();

    return type.transform((value, context) => {
      if (!value && options.nullable) return null;

      try {
        if (isString(value)) {
          const stringTime = value.trim().slice(0, FORMAT.length);
          const time = DateTime.fromFormat(stringTime, FORMAT);
          if (!time.isValid) throw value;

          return time.toFormat(`${FORMAT}:00`) as OnyxTime;
        }

        const result = XLSX.SSF.format(FORMAT, value);
        if (!result) throw value;

        return `${result}:00` as OnyxTime;
      } catch (value) {
        context.addIssue({
          code: 'custom',
          message: `Invalid time (${value})`,
        });
        return z.NEVER;
      }
    }) as any;
  }

  public transformAddress<T extends OnyxAddressType>(
    value: string,
    context: z.RefinementCtx,
    type: T,
  ): Promise<OnyxAddressByType[T]> {
    return firstValueFrom(
      this.addressService.geocode([type], value, 1).pipe(
        catchError(() => of({ items: [] })),
        map(({ items }) => {
          if (items.length) return items[0];

          context.addIssue({
            code: 'custom',
            message: `Invalid address (${value})`,
          });
          return z.NEVER;
        }),
      ),
    );
  }

  public transformBase(query: string, context: z.RefinementCtx): Promise<Base> {
    return firstValueFrom(
      this.basesService.listBases().pipe(
        map((bases) => {
          const identifier = query.toLowerCase();
          const base = bases.find(
            ({ uuid, name }) =>
              uuid === identifier || name.toLowerCase() === identifier,
          );
          if (base) return base;

          context.addIssue({
            code: 'custom',
            message: `Base not found (${query})`,
          });
          return z.NEVER;
        }),
      ),
    );
  }

  public transformBranchByName(
    branchName: string,
    context: z.RefinementCtx,
    contractorUuid?: string,
  ): Promise<OnyxOption<string>> {
    const branches$ = contractorUuid
      ? this.contractorsService.getBranches(contractorUuid)
      : this.businessService.getBranches();

    return firstValueFrom(
      branches$.pipe(
        map((branches) => {
          branches[0].name = this.translateService.instant(branches[0].name);

          const normalizedBranchName =
            UtilityHelper.toCompareString(branchName);
          const matchedBranch = branches.find(
            (branch) =>
              UtilityHelper.toCompareString(branch.name) ===
              normalizedBranchName,
          );
          if (matchedBranch) return matchedBranch;

          context.addIssue({
            code: 'custom',
            message: `Branch not found (${branchName})`,
          });

          return z.NEVER;
        }),
      ),
    );
  }

  public transformCompany(
    country: string,
    vatId: string,
    context: z.RefinementCtx,
    options?: { companyName: string | null; showFallback: boolean },
  ): Promise<CompanyData> {
    return firstValueFrom(
      this.dataProvidersService.getCompanyData(country, vatId).pipe(
        catchError(() => of(null)),
        map((data) => {
          if (data) return data;

          const companyName = options?.companyName;
          if (companyName) {
            return { companyName, vatId, country } as CompanyData;
          }

          context.addIssue({
            path: ['vatId'],
            code: 'custom',
            message: `Company not found (${country.toUpperCase()}${vatId})${options?.showFallback ? ' and no full name provided' : ''}`,
          });
          return z.NEVER;
        }),
      ),
    );
  }

  public transformCompanyDictionary<
    T extends CompanyDictionaryCode,
    Multiple extends boolean = false,
  >(
    value: string,
    context: z.RefinementCtx,
    options: { code: T; multiple?: Multiple },
  ): Multiple extends false
    ? CompanyDictionaryByCode[T]
    : CompanyDictionaryByCode[T][] {
    const multiple = options.multiple ?? (false as Multiple);
    const stop$ = new Subject<void>();
    return firstValueFrom(
      forkJoin(
        value.split(',').map((uuid) =>
          this.companyDictionariesService.getDictionary<T>(uuid).pipe(
            catchError(() => {
              context.addIssue({
                code: 'custom',
                message: `Company dictionary not found (${uuid})`,
              });
              stop$.next();
              return of(null);
            }),
            defaultIfEmpty(null),
            takeUntil(stop$),
          ),
        ),
      ).pipe(
        map((result) =>
          result.includes(null) ? z.NEVER : multiple ? result : result[0],
        ),
      ),
      { defaultValue: null },
    ) as any;
  }

  public transformCompanyDictionaryByName<
    T extends CompanyDictionaryCode,
    Multiple extends boolean = false,
  >(
    value: string,
    context: z.RefinementCtx,
    options: { code: T; multiple?: Multiple },
  ): Multiple extends false
    ? CompanyDictionaryByCode[T]
    : CompanyDictionaryByCode[T][] {
    const multiple = options.multiple ?? (false as Multiple);
    const stop$ = new Subject<void>();

    return firstValueFrom(
      this.companyDictionariesService.listDictionary(options.code).pipe(
        switchMap((items) =>
          forkJoin(
            value.split(',').map((dictionaryName) => {
              const name = UtilityHelper.toCompareString(dictionaryName);

              const matchingDictionary = items.find((item) => {
                const itemName = UtilityHelper.toCompareString(
                  this.languagePipe.transform(item.names),
                );
                return itemName === name;
              });

              if (!matchingDictionary) {
                context.addIssue({
                  code: 'custom',
                  message: `Company dictionary not found (${dictionaryName})`,
                });
                stop$.next();
                return of(null);
              }

              return of(matchingDictionary);
            }),
          ).pipe(takeUntil(stop$)),
        ),
        map((result) =>
          result.includes(null) ? z.NEVER : multiple ? result : result[0],
        ),
      ),
      { defaultValue: null },
    ) as any;
  }

  public transformContractors(
    contractors: string[],
    context: z.RefinementCtx,
  ): Promise<Contractor[]> {
    const stop$ = new Subject<void>();
    return firstValueFrom(
      forkJoin(
        contractors.map((contractor) =>
          this.contractorsService.getContractor(contractor).pipe(
            catchError(() => {
              context.addIssue({
                code: 'custom',
                message: `Contractor not found (${contractor})`,
              });
              stop$.next();
              return of(null);
            }),
            takeUntil(stop$),
          ),
        ),
      ).pipe(
        map((result) =>
          result.includes(null) ? z.NEVER : (result as Contractor[]),
        ),
      ),
      { defaultValue: [] },
    );
  }

  public transformContractorByVatId(
    vatId: string,
    context: z.RefinementCtx,
  ): Promise<Contractor> {
    const country = vatId.slice(0, 2).toLowerCase();
    const vatIdNumber = vatId.slice(2);

    return firstValueFrom(
      this.contractorsService
        .listContractors({
          filters: ContractorHelper.createVatIdFilters(country, vatIdNumber),
          page: 1,
          limit: Number.MAX_SAFE_INTEGER,
        })
        .pipe(
          catchError(() => {
            context.addIssue({
              code: 'custom',
              message: `Contractor not found (${vatId})`,
            });
            return of(null);
          }),
        )
        .pipe(map((result) => result?.items[0] ?? z.NEVER)),
    );
  }

  public transformDateRange(
    from: string,
    to: string,
    context: z.RefinementCtx,
  ): OnyxDate {
    if (to === 'indefinite') return { from, to: null };
    if (from <= to) return { from, to };

    context.addIssue({
      code: 'custom',
      message: `Invalid date range (from: ${from}, to: ${to})`,
    });
    return z.NEVER;
  }

  public transformDictionary<
    T extends DictionaryCode,
    Multiple extends boolean = false,
  >(
    value: string | number,
    context: z.RefinementCtx,
    options: {
      code: OnyxMaybeArray<T>;
      multiple?: Multiple;
      path?: string;
    },
  ): Promise<
    Multiple extends false
      ? DictionaryResponseByCode[T]['value']
      : DictionaryResponseByCode[T]['value'][]
  > {
    const multiple = options.multiple ?? (false as Multiple);
    return firstValueFrom(
      of(options.code).pipe(
        map((code) => castArray(code)),
        switchMap((codes) =>
          codes.length
            ? forkJoin(
                codes.map((code) =>
                  this.dictionariesService.getDictionary(code),
                ),
              )
            : of([]),
        ),
        map((dictionaries) =>
          chain(dictionaries)
            .map((dictionary) => DropdownHelper.mapToOptions(dictionary as any))
            .compact()
            .flatten()
            .map((option) => option as DictionaryResponseByCode[T])
            .value(),
        ),
        map((allowedOptions) => {
          const mappedValues = new Map(
            allowedOptions.map((item) => [item.value.toString(), item.value]),
          );
          const mappedNames = new Map(
            allowedOptions.map((item) => [
              item.name.toString().toLowerCase(),
              item.value,
            ]),
          );

          const items = chain(value)
            .thru((value) => {
              const stringValue = String(value).trim();
              return multiple ? stringValue.split(',') : [stringValue];
            })
            .map((item) => item.trim().toLowerCase())
            .value();
          const values = items.map((item) => {
            const mappedValue = mappedValues.get(item);
            if (mappedValue != null) return mappedValue;

            return mappedNames.get(item) ?? item;
          });
          const invalidValues = values.filter(
            (value) => !mappedValues.has(String(value)),
          );

          if (invalidValues.length) {
            context.addIssue({
              code: 'custom',
              message: `Invalid dictionary values (received: ${items
                .map((item) => `'${item}'`)
                .join(', ')}, expected: ${allowedOptions
                .map(({ value, name }) => `'${value}' (${name})`)
                .join(', ')})`,
              ...(options.path && { path: [options.path] }),
            });
            return z.NEVER;
          }
          return multiple ? values : (values[0] as any);
        }),
      ),
    );
  }

  public transformNumberRange(
    from: number,
    to: number,
    context: z.RefinementCtx,
  ): OnyxNumberRange {
    if (from <= to) return { from, to };

    context.addIssue({
      code: 'custom',
      message: `Invalid number range (from: ${from}, to: ${to})`,
    });
    return z.NEVER;
  }

  public transformPeselNumber(value: string, context: z.RefinementCtx): string {
    if (PeselNumberHelper.isValid(value)) return value;

    context.addIssue({
      code: 'custom',
      message: `Invalid PESEL number (${value})`,
    });
    return z.NEVER;
  }

  public transformPointsOfInterest(
    pointsOfInterest: string[],
    context: z.RefinementCtx,
  ): Promise<PointOfInterest[]> {
    const stop$ = new Subject<void>();
    return firstValueFrom(
      forkJoin(
        pointsOfInterest.map((pointOfInterest) =>
          this.pointsOfInterestService.getPointOfInterest(pointOfInterest).pipe(
            catchError(() => {
              context.addIssue({
                code: 'custom',
                message: `Point of interest not found (${pointOfInterest})`,
              });
              stop$.next();
              return of(null);
            }),
            takeUntil(stop$),
          ),
        ),
      ).pipe(
        map((result) =>
          result.includes(null) ? z.NEVER : (result as PointOfInterest[]),
        ),
      ),
      { defaultValue: [] },
    );
  }

  public transformTimeRange(
    from: OnyxTime,
    to: OnyxTime,
    context: z.RefinementCtx,
  ): OnyxTimeRange {
    if (from <= to) return { from, to };

    context.addIssue({
      code: 'custom',
      message: `Invalid time range (from: ${from}, to: ${to})`,
    });
    return z.NEVER;
  }

  public transformVinNumber(value: string, context: z.RefinementCtx): string {
    const vinNumber = value.toUpperCase();
    if (VIN_NUMBER_PATTERN.test(vinNumber)) return vinNumber;

    context.addIssue({
      code: 'custom',
      message: `Invalid VIN number (${vinNumber})`,
    });
    return z.NEVER;
  }

  public transformRoute(
    dto: OrderFormDto,
    context: z.RefinementCtx,
  ): Promise<{
    route: Omit<OrderRoute, 'author'> | null;
    points: OnyxRoutePoint[];
  }> {
    const { vehicleTypes, rate } = dto.basicInformation;

    return firstValueFrom(
      OrderFormRouteConfigModalComponent.getDefaultConfig(this.injector, {
        vehicleTypes,
        resultCurrency: rate.currency!,
      }).pipe(
        map((routeConfig): CalculateRouteRequest => ({
          ...routeConfig,
          points: (
            dto.points as (OrderLoadingPointForm | OrderUnloadingPointForm)[]
          ).map((point) => ({
            category: point.category,
            coordinates: point.address!.coordinates,
          })),
        })),
        switchMap((routeConfig) =>
          this.addressService.calculateRoute(routeConfig).pipe(
            catchError(() => of(null)),
            map((response) => {
              const route = response?.routes?.at(0);
              if (!route) {
                context.addIssue({
                  code: 'custom',
                  message: `Route not found`,
                });
                return z.NEVER;
              } else if (!route.summary.distance) {
                context.addIssue({
                  code: 'custom',
                  message: `Route is empty`,
                });
                return z.NEVER;
              }

              return {
                route: {
                  config: routeConfig,
                  updatedAt: DateTime.now().toISO(),
                  isBlocked: false,
                  summary: route.summary,
                  isVerified: false,
                },
                points: route.points,
              };
            }),
          ),
        ),
      ),
    );
  }

  public refineValidationFields<T>(
    dto: T,
    context: z.RefinementCtx,
    validationFields: Record<string, ValidationField>,
    fields: Record<string, Paths<T>>,
    mapper?: Partial<Record<Paths<T>, string>>,
  ): void {
    for (const [path, field] of Object.entries(fields)) {
      const key = mapper?.[field] ?? String(field).split('.').at(-1)!;
      this.refineValidationField(
        get(dto, field),
        context,
        validationFields[key] ?? this.FORBIDDEN_FIELD,
        path,
      );
    }
  }

  public refineValidationField(
    value: any,
    context: z.RefinementCtx,
    field: ValidationField,
    path: string,
  ): void {
    const checkInList = (allowedValues: any[]): void => {
      const values = castArray(value);
      const invalidValues = values.filter(
        (value) => !allowedValues.includes(value),
      );

      if (invalidValues.length) {
        throw `Invalid value (received: ${values
          .map((value) => `'${value}'`)
          .join(', ')}, expected: ${allowedValues
          .map((value) => `'${value}'`)
          .join(', ')})`;
      }
    };

    try {
      switch (field.type) {
        case 'forbidden':
          if (value != null) throw `Forbidden value in this context (${value})`;
          return;

        case 'conditional':
        case 'optional':
          return;

        case 'optional-in-list':
          if (value != null) checkInList(field.values);
          return;

        case 'required':
          if (value == null) throw `Required value in this context`;
          return;

        case 'required-in-list':
          if (value == null) throw `Required value in this context`;
          checkInList(field.values);
          return;

        case 'required-in-range':
          if (value < field.min || value > field.max) {
            throw `Value must be in range (received: ${value}, expected: ${field.min} - ${field.max})`;
          }
          return;
      }
    } catch (message) {
      context.addIssue({
        path: [path],
        code: 'custom',
        message: message as string,
      });
    }
  }

  public refineEmails(
    emails: string[] | null,
    context: z.RefinementCtx,
    path: string,
  ): void {
    if (emails == null) return;

    for (const email of emails) {
      const result = z.email().safeParse(email);
      if (result.success) continue;

      context.addIssue({
        path: [path],
        code: 'custom',
        message: `Invalid e-mail address (${email})`,
      });
    }
  }

  public refineOptionalField<Path extends string>(
    params: RefineOptionalFieldParams<Path>,
  ): void {
    const {
      isRequired,
      value,
      context,
      path,
      missingMessage,
      forbiddenMessage,
    } = params;

    if (isRequired && !value) {
      context.addIssue({ path, code: 'custom', message: missingMessage });
    } else if (!isRequired && value) {
      context.addIssue({ path, code: 'custom', message: forbiddenMessage });
    }
  }

  public inject<T>(token: ProviderToken<T>): T {
    return runInInjectionContext(this.injector, () => inject(token));
  }
}
